import { type BaClickInput, BaClickType, BaInpaintTaskStatus, type BaMaskType, type OnnxModelScale } from '@/typings/ai'
import type { InpaintImageInfo, SavedImageInfo } from '@/typings/image'
import ImageProcessor from '@/utils/ai/ImageProcessor'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { cloneDeep } from 'lodash-es'
import { type RefImage, type SettingImage } from '@/typings/setting'
import type { TaskQueue } from '@/typings/web-socket'
import type { Project } from '@/typings/project'

export const useImageStore = defineStore(
  'image',
  () => {
    /**
     * 被选中的图片，显示在中间区域的图片对象
     */
    const selectedImage = ref<InpaintImageInfo | null>(null)
    /**
     * 被选中的预览图片，显示在中间区域的左部
     */
    const selectedPreviewImage = ref<SettingImage | RefImage | null>(null)
    /**
     * 历史图片的列表
     */
    const imageInfoList = ref<InpaintImageInfo[]>([])
    /**
     * 生成图片的列表
     */
    const inpaintImageInfoList = ref<InpaintImageInfo[]>([])
    /**
     * 选区图片ID
     */
    const mask = ref<string | undefined>()
    /**
     * 选区操作类型，无、鼠标放置、点击
     */
    const maskType = ref<BaMaskType>('none')
    /**
     * 选区面积
     */
    const modelScale = ref<OnnxModelScale | null>(null)
    /**
     * 微调选区点击操作数据
     */
    const clicks = ref<BaClickInput[]>([])
    /**
     * 当前微调选区操作类型，添加选区、减少选区
     */
    const clickType = ref<BaClickType>(BaClickType.nop)
    /**
     * 当前正在进行的图片生成任务
     */
    const imageProcessor = ref<ImageProcessor>(new ImageProcessor({}))
    /**
     * 手绘蒙版
     */
    const maskFree = ref<string>('')
    /**
     * 图片处理的任务ID集合
     */
    const taskQueue = ref<TaskQueue[]>([])
    /**
     * 相册列表
     */
    const savedImageList = ref<SavedImageInfo[]>([])
    /**
     * 首页图片列表
     */
    const allSavedImageList = ref<SavedImageInfo[]>([])
    /**
     * 项目列表
     */
    const projectList = ref<Project[]>([])
    /**
     * 当前项目下，首页图片列表
     */
    const currentProjectSavedImageList = ref<SavedImageInfo[]>([])
    /**
     * 画布状态，用于持久化画布
     */
    const compositorCanvasState = ref<string>('')

    /**
     * 显示在最右侧的图片列表，包含了生成图片列表和历史图片列表
     */
    const footerImageInfoList = computed(() => [...inpaintImageInfoList.value, ...imageInfoList.value])

    const setSelectedImage = (imageInfo: InpaintImageInfo | null) =>
      (selectedImage.value = cloneDeep(imageInfo))
    const setSelectedPreviewImage = (image: SettingImage | RefImage | null) =>
      (selectedPreviewImage.value = cloneDeep(image))
    const updateSelectedImageStatus = (status: BaInpaintTaskStatus | null) =>
      (selectedImage.value!.status = status)
    const setImageInfoList = (imageList: InpaintImageInfo[]) => (imageInfoList.value = imageList)
    const setInpaintImageInfoList = (imageInfoList: InpaintImageInfo[]) =>
      (inpaintImageInfoList.value = imageInfoList)
    const setMask = (m?: string) => (mask.value = m)
    const setMaskType = (type: BaMaskType) => (maskType.value = type)
    const setModelScale = (scale: OnnxModelScale) => (modelScale.value = scale)
    const setClicks = (cls: BaClickInput[]) => (clicks.value = cls)
    const setClickType = (type: BaClickType) => (clickType.value = type)
    const setImageProcessor = (processor: ImageProcessor) => (imageProcessor.value = processor)
    const setMaskFree = (m: string) => (maskFree.value = m)
    const addTaskQueue = (queue: TaskQueue) => {
      taskQueue.value.push(queue)
      console.info(`Task queue added: taskId【${queue.queueId}】, resourceId【${queue.resourceId}】`)
    }
    const deleteTaskQueue = (queueId: string, resourceId?: string) =>
      taskQueue.value.some((queue, index) => {
        if (queue.queueId === queueId && queue.resourceId === resourceId) {
          taskQueue.value.splice(index, 1)
          console.info(`Task queue removed: taskId【${queue.queueId}】, resourceId【${queue.resourceId}】`)
          return true
        }
        return false
      })
    /**
     * 重置选区数据
     */
    const resetClicks = () => {
      clicks.value = []
      imageProcessor.value.resetMask()
      mask.value = undefined
      maskType.value = 'none'
    }
    const setSavedImageList = (list: SavedImageInfo[]) => savedImageList.value = list
    const setAllSavedImageList = (list: SavedImageInfo[]) => allSavedImageList.value = list
    const setProjectList = (list: Project[]) => projectList.value = list
    const setCurrentProjectSavedImageList = (list: SavedImageInfo[]) => currentProjectSavedImageList.value = list
    /**
     * 设置画布状态
     */
    const setCompositorCanvasState = (state: string) => compositorCanvasState.value = state

    return {
      selectedImage,
      selectedPreviewImage,
      imageInfoList,
      footerImageInfoList,
      inpaintImageInfoList,
      mask,
      maskType,
      modelScale,
      clicks,
      clickType,
      imageProcessor,
      maskFree,
      taskQueue,
      savedImageList,
      allSavedImageList,
      projectList,
      currentProjectSavedImageList,
      compositorCanvasState,
      setSelectedImage,
      setSelectedPreviewImage,
      updateSelectedImageStatus,
      setImageInfoList,
      setInpaintImageInfoList,
      setMask,
      setMaskType,
      setModelScale,
      setClicks,
      setClickType,
      setImageProcessor,
      setMaskFree,
      addTaskQueue,
      deleteTaskQueue,
      resetClicks,
      setSavedImageList,
      setAllSavedImageList,
      setProjectList,
      setCurrentProjectSavedImageList,
      setCompositorCanvasState
    }
  },
  {
    persist: {
      paths: ['taskQueue', 'imageInfoList', 'savedImageList', 'allSavedImageList', 'projectList', 'currentProjectSavedImageList', 'compositorCanvasState']
    }
  }
)
